import type { ProtoRadarData } from "@catbento/shared";
import type { RawPlayerRecord } from "./snapshot.js";

// Pure lookup for radar (Phase 02c). Reproduces renderers-radar.js's axisRawValue:
// radarMetrics wins per key, else the flat stats value. Merging the two maps
// (radarMetrics over stats) is exactly that rule for every key at once, and is the
// "drop-in replacement for radarMetrics" the renderer builds per render.

type Players = Record<string, RawPlayerRecord>;

/**
 * One player's radar values for a season (radarMetrics merged over stats), plus
 * season + template metadata. Season resolution matches the stats hook. Returns
 * null if the name isn't in the snapshot.
 */
export function getRadarData(
  players: Players,
  name: string,
  season?: string
): ProtoRadarData | null {
  const p = players[name];
  if (!p) return null;

  const seasons = p.seasons ?? {};
  const seasonCodes = Object.keys(seasons).sort((a, b) => b.localeCompare(a));
  const currentSeason = p.currentSeason ?? seasonCodes[0] ?? "";
  const seasonCode = season && seasons[season] ? season : currentSeason;

  const block = seasons[seasonCode] ?? {};
  // radarMetrics overrides stats (authoritative capture); stats fills the rest.
  const values: Record<string, number | null> = {
    ...(block.stats ?? {}),
    ...(block.radarMetrics ?? {}),
  };

  return {
    seasonCodes,
    seasonCode,
    currentSeason,
    positionTemplate: p.positionTemplate ?? null,
    values,
    // The gate is the RAW radarMetrics blob, not the merged values (see the contract).
    hasRadarMetrics: Object.keys(block.radarMetrics ?? {}).length > 0,
  };
}
